if (!customElements.get('worth-card-variants')) {
  customElements.define(
    'worth-card-variants',
    class WorthCardVariants extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';

        const json = this.querySelector('[data-worth-card-variants-json]');
        if (!json) return;

        try {
          this.variants = JSON.parse(json.textContent);
        } catch (error) {
          console.error('Unable to load product-card variants.', error);
          return;
        }

        this.card = this.closest('.card');
        this.colorPosition = Number(this.dataset.colorPosition);
        this.sizePosition = Number(this.dataset.sizePosition);
        this.variantInput = this.querySelector('[data-worth-card-variant-id]');
        this.form = this.querySelector('form');
        this.addButton = this.querySelector('.worth-card-add');
        this.buttonLabel = this.querySelector('[data-worth-card-button-label]');
        this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
        this.errorMessage = this.querySelector('.product-form__error-message');
        this.optionButtons = Array.from(this.querySelectorAll('[data-worth-option-position]'));
        this.selectedOptions = Array(this.variants[0]?.options?.length || 0).fill(null);

        const initialVariant =
          this.variants.find((variant) => variant.available) || this.variants[0];

        if (initialVariant) {
          initialVariant.options.forEach((value, index) => {
            if (index !== this.sizePosition) this.selectedOptions[index] = value;
          });
        }

        this.optionButtons.forEach((button) => {
          button.addEventListener('click', () => this.onOptionClick(button));
        });
        if (this.addButton) {
          this.addButton.addEventListener('click', (event) => this.onAddClick(event));
        }
        if (this.form) {
          this.form.addEventListener('submit', (event) => this.onFormSubmit(event), true);
        }

        this.syncSelectedState();
        this.updateAvailability();
        this.updateCard();
        this.listenForCartEvents();
      }

      onAddClick(event) {
        if (!this.requiresSizeSelection()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.promptForSize();
      }

      onFormSubmit(event) {
        if (!this.requiresSizeSelection()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.promptForSize();
      }

      requiresSizeSelection() {
        return (
          this.sizePosition >= 0 &&
          this.selectedOptions[this.sizePosition] === null
        );
      }

      promptForSize() {
        const sizes = this.querySelector('.worth-card-sizes');
        sizes?.classList.add('is-prompted');
        if (this.errorWrapper && this.errorMessage) {
          this.errorMessage.textContent = 'Please select a size first.';
          this.errorWrapper.hidden = false;
        }
        this.showTemporaryButtonMessage('SELECT A SIZE FIRST');

        window.setTimeout(() => {
          sizes?.classList.remove('is-prompted');
        }, 1600);
      }

      onOptionClick(button) {
        if (button.disabled || button.classList.contains('is-unavailable')) return;

        const position = Number(button.dataset.worthOptionPosition);
        const value = button.dataset.worthOptionValue;
        this.selectedOptions[position] = value;

        if (position === this.sizePosition && this.errorWrapper) {
          this.errorWrapper.hidden = true;
        }

        if (position === this.colorPosition && this.sizePosition >= 0) {
          const selectedSize = this.selectedOptions[this.sizePosition];
          const sizeStillAvailable =
            selectedSize &&
            this.variants.some(
              (variant) =>
                variant.available &&
                variant.options[position] === value &&
                variant.options[this.sizePosition] === selectedSize
            );

          if (!sizeStillAvailable) this.selectedOptions[this.sizePosition] = null;
        }

        this.syncSelectedState();
        this.updateAvailability();
        this.updateCard();
      }

      syncSelectedState() {
        this.optionButtons.forEach((button) => {
          const position = Number(button.dataset.worthOptionPosition);
          const selected = this.selectedOptions[position] === button.dataset.worthOptionValue;
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-checked', selected ? 'true' : 'false');
        });
      }

      updateAvailability() {
        this.optionButtons.forEach((button) => {
          const position = Number(button.dataset.worthOptionPosition);
          const value = button.dataset.worthOptionValue;
          const available = this.variants.some((variant) => {
            if (!variant.available || variant.options[position] !== value) return false;

            return this.selectedOptions.every((selectedValue, optionIndex) => {
              if (optionIndex === position || selectedValue === null) return true;
              if (position === this.colorPosition && optionIndex === this.sizePosition) return true;
              return variant.options[optionIndex] === selectedValue;
            });
          });

          button.disabled = !available;
          button.classList.toggle('is-unavailable', !available);
          button.setAttribute('aria-disabled', available ? 'false' : 'true');
        });
      }

      updateCard() {
        const complete = this.selectedOptions.every((value) => value !== null);
        const exactVariant = complete
          ? this.variants.find((variant) =>
              variant.options.every((value, index) => value === this.selectedOptions[index])
            )
          : null;

        const previewVariant =
          exactVariant ||
          this.variants.find(
            (variant) =>
              variant.available &&
              this.selectedOptions.every(
                (selectedValue, index) =>
                  selectedValue === null || variant.options[index] === selectedValue
              )
          ) ||
          this.variants.find((variant) => variant.available) ||
          this.variants[0];

        if (previewVariant) {
          this.updateImage(previewVariant);
          this.updatePrice(previewVariant);
          this.updateSaleBadge(previewVariant);
          this.updateProductLinks(previewVariant);
        }

        if (!this.variantInput || !this.addButton || !this.buttonLabel) return;

        const purchasable = Boolean(exactVariant?.available);
        const hasAvailableVariant = this.variants.some((variant) => variant.available);
        this.variantInput.disabled = !purchasable;
        this.addButton.disabled = !hasAvailableVariant;

        if (exactVariant) this.variantInput.value = exactVariant.id;

        if (!hasAvailableVariant) {
          this.buttonLabel.textContent = 'SOLD OUT';
        } else {
          this.buttonLabel.textContent = 'QUICK ADD';
        }
      }

      updateImage(variant) {
        if (!variant.image || !this.card) return;
        const image = this.card.querySelector('.card__media .media > img:first-child');
        if (!image) return;

        image.src = variant.image;
        if (variant.imageSrcset) image.srcset = variant.imageSrcset;
        if (variant.imageAlt) image.alt = variant.imageAlt;
      }

      updatePrice(variant) {
        if (!this.card) return;
        const price = this.card.querySelector('.price');
        if (!price) return;

        const regularPrice = price.querySelector('.price__regular .price-item--regular');
        const salePrice = price.querySelector('.price__sale .price-item--sale');
        const comparePrice = price.querySelector('.price__sale .price-item--regular');
        const onSale =
          variant.compareAtPriceAmount &&
          Number(variant.compareAtPriceAmount) > Number(variant.priceAmount);

        price.classList.toggle('price--on-sale', Boolean(onSale));
        price.classList.toggle('price--sold-out', !variant.available);

        if (regularPrice) regularPrice.textContent = variant.price;
        if (salePrice) salePrice.textContent = variant.price;
        if (comparePrice) comparePrice.textContent = onSale ? variant.compareAtPrice : '';
      }

      updateSaleBadge(variant) {
        if (!this.card) return;
        const badges = this.card.querySelectorAll('[data-worth-card-sale-badge]');
        const onSale =
          variant.compareAtPriceAmount &&
          Number(variant.compareAtPriceAmount) > Number(variant.priceAmount);

        badges.forEach((badge) => {
          badge.hidden = !onSale;
          if (!onSale) return;

          const percentage = Math.round(
            ((Number(variant.compareAtPriceAmount) - Number(variant.priceAmount)) /
              Number(variant.compareAtPriceAmount)) *
              100
          );
          badge.textContent = `SAVE ${percentage}%`;
        });
      }

      updateProductLinks(variant) {
        if (!this.card || !variant?.id) return;
        const baseUrl = this.dataset.productUrl;
        this.card.querySelectorAll('a.full-unstyled-link').forEach((link) => {
          link.href = `${baseUrl}?variant=${variant.id}`;
        });
      }

      listenForCartEvents() {
        if (
          !this.variantInput ||
          typeof subscribe !== 'function' ||
          typeof PUB_SUB_EVENTS === 'undefined'
        ) {
          return;
        }

        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (
            String(event.productVariantId) !== String(this.variantInput.value) ||
            event.source !== 'product-form'
          ) {
            return;
          }

          this.showTemporaryButtonMessage('ADDED ✓');
        });
      }

      showTemporaryButtonMessage(message) {
        if (!this.buttonLabel) return;
        window.clearTimeout(this.messageTimer);
        this.buttonLabel.textContent = message;
        this.messageTimer = window.setTimeout(() => {
          this.buttonLabel.textContent = 'QUICK ADD';
        }, 1600);
      }
    }
  );
}
